
import { ChangeEvent } from "react";
import {
  IPropsFieldValue,
  IPropsInputChange,
  IPropsSelectChange,
  ValueType,
  TextInputValue,
  CheckboxValue
} from "./fieldPropertiesInterfaceNew";

/**
 * Helper para manejar valores del modelo y eventos de cambio.
 * Soporta:
 * - Inputs de texto (string, number)
 * - Checkbox / Toggle (boolean)
 * - Select
 */
export default function modelHelper({
  model,
  attr,
  value,
  onChange,
  emptyClearStates
}: IPropsFieldValue & (IPropsInputChange | IPropsSelectChange)) {
  if (!model && value === undefined) {
    throw new Error("You should provide a model property or a value.");
  }

  // Si el modelo es un hook [state, setState]
  const [state, setState] = model || [undefined, undefined];

  /**
   * Maneja el cambio genérico en inputs/select/textarea.
   * Actualiza el modelo y dispara onChange si existe.
   */
  const onValueChange = (
    event: ChangeEvent<HTMLInputElement> &
      ChangeEvent<HTMLSelectElement> &
      ChangeEvent<HTMLTextAreaElement>
  ): void => {
    onChange?.(event);
    if (state && attr) {
      setValue(event.target.value);
    }
  };

  /**
   * Obtiene el valor actual del campo (sin conversión).
   */
  const getValue = (): ValueType => {
    if (value !== undefined) return value;
    if (state && attr) return state[attr];
    return "";
  };

  /**
   * Obtiene el valor como texto (para inputs).
   * Si es boolean, lo convierte a "true"/"false".
   */
  const getTextValue = (): TextInputValue => {
    const val = getValue();
    return typeof val === "boolean" ? (val ? "true" : "false") : val;
  };

  /**
   * Obtiene el valor como boolean (para checkbox/toggle).
   * Si es string "true", lo interpreta como true.
   */
  const getBooleanValue = (): CheckboxValue => {
    const val = getValue();
    return val === true || val === "true";
  };

  /**
   * Setea el valor en el modelo.
   * Puede ser string, number o boolean.
   */
  const setValue = (newValue: ValueType): void => {
    if (value !== undefined) {
      value = newValue;
      return;
    }
    if (state && attr) {
      const modelWithChange = { ...state };
      modelWithChange[attr] = newValue;
      setState(modelWithChange);
    }
  };

  /**
   * Limpia el valor del atributo (usa emptyClearStates si existe).
   */
  const singleClearState = (): void => {
    if (!attr) return;
    const emptyValue = emptyClearStates?.[attr] ?? "";
    setValue(emptyValue);
  };

  return {
    onValueChange,
    getValue,
    getTextValue,
    getBooleanValue,
    setValue,
    singleClearState
  };
}